import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	getMomentTz,
	useUserContext,
} from "@dexteel/mesf-core";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableRowsIcon from "@mui/icons-material/TableRows";
import {
	Divider,
	Grid2,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import type { Moment } from "moment-timezone";
import { useEffect, useState } from "react";
import { DelaysCategoryBreakdown } from "./components/DelaysCategoryBreakdown";
import { DelaysCrewShiftBreakdown } from "./components/DelaysCrewShiftBreakdown";
import { DelaysDailyTrend } from "./components/DelaysDailyTrend";
import { DelaysReportTable } from "./components/DelaysReportTable";
import { DelaysSummary } from "./components/DelaysSummary";
import { Filters, ViewType } from "./components/filters/Filters";
import { DelayByShift } from "./models/DelayByShift";
import { getDelays } from "./repositories/DelaysReportRepository";

const moment = getMomentTz();

export type DelayReportFilters = {
	startDate: Moment;
	endDate: Moment;
	delayAreaId: number | null;
	delayCodeId: number | null;
	delayCategoryId: number | null;
	sectionId: number | null;
	productionEventId: number | null;
	productionEventMillAssetId: number | null;
	crew: string | null;
	shift: string | null;
	includeMicrodelays: boolean;
};

const getInitialFilters = (defaultAreaId?: number): DelayReportFilters => ({
	startDate: moment().subtract(7, "days").startOf("day"),
	endDate: moment().endOf("day"),
	delayAreaId: defaultAreaId ?? null,
	delayCodeId: null,
	delayCategoryId: null,
	sectionId: null,
	productionEventId: null,
	productionEventMillAssetId: null,
	crew: "all",
	shift: "all",
	includeMicrodelays: false,
});

type useDelaysProps = {
	startDate: Date;
	endDate: Date;
	delayAreaAssetId?: number | null;
	includeMicrodelays?: boolean;
	shift?: string;
	crew?: string;
	delayCodeId?: number | null;
	delayCategoryId?: number | null;
	sectionId?: number | null;
	productionEventId?: number | null;
	productionEventMillAssetId?: number | null;
	onError: (error: Error) => void;
};

export const useDelays = ({
	startDate,
	endDate,
	delayAreaAssetId,
	includeMicrodelays = false,
	shift,
	crew,
	delayCodeId,
	delayCategoryId,
	sectionId,
	productionEventId,
	productionEventMillAssetId,
	onError,
}: useDelaysProps) => {
	return useQuery<DelayByShift[], Error>({
		queryKey: [
			"delays",
			startDate,
			endDate,
			delayAreaAssetId,
			includeMicrodelays,
			shift,
			crew,
			delayCodeId,
			delayCategoryId,
			sectionId,
			productionEventId,
			productionEventMillAssetId,
		],
		queryFn: ({ signal }) =>
			getDelays(
				{
					startDate,
					endDate,
					delayAreaAssetId,
					includeMicrodelays,
					shift,
					crew,
					delayCodeId,
					delayCategoryId,
					sectionId,
					productionEventId,
					productionEventMillAssetId,
				},
				signal,
			),
		onError: onError,
		enabled: true,
	});
};

export const DelaysReportPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const [filters, setFilters] = useState<DelayReportFilters>(() =>
		getInitialFilters(Number(defaultAreaId) || undefined),
	);

	useEffect(() => {
		if (defaultAreaId && filters.delayAreaId === null) {
			setFilters((prev) => ({
				...prev,
				delayAreaId: Number(defaultAreaId) || null,
			}));
		}
	}, [defaultAreaId]);

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [mainMode, setMainMode] = useState<"table" | "views">("table");
	const [selectedView, setSelectedView] = useState<ViewType>("summary");

	const { data: delays, isLoading } = useDelays({
		startDate: filters.startDate.toDate(),
		endDate: filters.endDate.toDate(),
		delayAreaAssetId: filters.delayAreaId,
		includeMicrodelays: filters.includeMicrodelays,
		shift: filters.shift as string,
		crew: filters.crew as string,
		delayCodeId: filters.delayCodeId,
		delayCategoryId: filters.delayCategoryId,
		sectionId: filters.sectionId,
		productionEventId: filters.productionEventId,
		productionEventMillAssetId: filters.productionEventMillAssetId,
		onError: (e) => setError(e.message),
	});

	const handleFilterChange = (updates: Partial<DelayReportFilters>) => {
		setFilters((prev) => {
			const next = { ...prev, ...updates };

			if (
				(Object.prototype.hasOwnProperty.call(updates, "sectionId") &&
					updates.sectionId !== prev.sectionId) ||
				(Object.prototype.hasOwnProperty.call(updates, "delayAreaId") &&
					updates.delayAreaId !== prev.delayAreaId)
			) {
				next.productionEventId = null;
				next.productionEventMillAssetId = null;
			}

			return next;
		});
	};

	const handleExportToExcel = () => {
		if (gridApi) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: "delays_report.xlsx",
			};
			gridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	const renderViewContent = () => {
		if (isLoading) {
			return <CenteredLazyLoading />;
		}

		switch (selectedView) {
			case "summary":
				return <DelaysSummary delays={delays || []} />;
			case "trend":
				return <DelaysDailyTrend delays={delays || []} />;
			case "category":
				return <DelaysCategoryBreakdown delays={delays || []} />;
			case "crewShift":
				return <DelaysCrewShiftBreakdown delays={delays || []} />;
			default:
				return null;
		}
	};

	return (
		<>
			<HelmetDexteel title="Delays Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{
					alignContent: "flex-start",
					backgroundColor: pageBgColor,
					minHeight: masterDetailsMinHeight,
				}}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Delays Report
					</Typography>
				</Grid2>

				<Grid2
					container
					spacing={1}
					size={{ xs: 12 }}
					alignItems="flex-start"
					justifyContent="space-between"
				>
					<Grid2 size={{ md: 8 }}>
						<Filters
							filters={filters}
							onFilterChange={handleFilterChange}
							mainMode={mainMode}
							selectedView={selectedView}
							setSelectedView={setSelectedView}
						/>
					</Grid2>
					<Grid2
						size={{ md: 4 }}
						container
						flexDirection="column"
						alignItems="flex-end"
						justifyContent="flex-start"
						spacing={1}
						sx={{ mt: 1 }}
					>
						<Grid2 size={{ md: 3 }}>
							<ExcelButton
								handleExportToExcel={handleExportToExcel}
								disabled={!delays || delays.length === 0}
							/>
						</Grid2>
						<Grid2 size={{ md: 6 }}>
							<ToggleButtonGroup
								value={mainMode}
								exclusive
								onChange={(_, value) => value && setMainMode(value)}
								size="small"
								fullWidth
								sx={{
									"& .MuiToggleButton-root": {
										px: 1.5,
										gap: 0.5,
										textTransform: "none",
										fontWeight: 500,
										fontSize: "0.8125rem",
										whiteSpace: "nowrap",
									},
								}}
							>
								<ToggleButton value="table">
									<TableRowsIcon fontSize="small" />
									Table
								</ToggleButton>
								<ToggleButton value="views">
									<BarChartIcon fontSize="small" />
									Reports
								</ToggleButton>
							</ToggleButtonGroup>
						</Grid2>
					</Grid2>
				</Grid2>

				<Grid2 size={{ md: 12 }}>
					<Divider />
				</Grid2>

				<Grid2
					size={{ xs: 12 }}
					display={mainMode == "views" ? "block" : "none"}
				>
					{renderViewContent()}
				</Grid2>
				<Grid2
					size={{ xs: 12 }}
					display={mainMode == "table" ? "block" : "none"}
				>
					<DelaysReportTable
						isLoading={isLoading}
						rows={delays || []}
						setGridApi={setGridApi}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
